import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sensors } from '../services/api';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import './Dashboard.css';

function Dashboard() {
  const [sensorList, setSensorList] = useState([]);
  const [recentData, setRecentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSensors: 0,
    activeSensors: 0,
    totalReadings: 0,
    lastUpdate: null
  });
  
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Carregar sensores
      const sensorsResponse = await sensors.getAll();
      const sensorsData = sensorsResponse.data;
      setSensorList(sensorsData);

      // Carregar dados recentes de cada sensor
      let allRecentData = [];
      let totalReadings = 0;

      for (const sensor of sensorsData) {
        try {
          const dataResponse = await sensors.getData(sensor._id, { limit: 10 });
          const sensorData = dataResponse.data.map(item => ({
            ...item,
            sensorName: sensor.name,
            sensorType: sensor.type
          }));
          allRecentData = [...allRecentData, ...sensorData];
          totalReadings += dataResponse.data.length;
        } catch (error) {
          console.error(`Erro ao carregar dados do sensor ${sensor.name}:`, error);
        }
      }

      // Ordenar por data (mais recentes primeiro)
      allRecentData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setRecentData(allRecentData.slice(0, 20));

      // Atualizar estatísticas
      setStats({
        totalSensors: sensorsData.length,
        activeSensors: sensorsData.filter(s => s.active).length,
        totalReadings: totalReadings,
        lastUpdate: new Date().toLocaleString()
      });

    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Dados para o gráfico de pizza (tipos de sensores)
  const sensorTypeData = sensorList.reduce((acc, sensor) => {
    const type = sensor.type || 'other';
    const existing = acc.find(item => item.name === type);
    if (existing) {
      existing.value++;
    } else {
      acc.push({ name: type, value: 1 });
    }
    return acc;
  }, []);

  // Dados para o gráfico de últimas leituras
  const lastReadingsData = recentData.slice(0, 10).map(item => ({
    name: item.sensorName,
    value: item.value,
    time: new Date(item.timestamp).toLocaleTimeString()
  }));

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Carregando dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <div className="user-info">
          <span className="welcome-message">Bem-vindo, {user.username}!</span>
          <span className="last-update">Última atualização: {stats.lastUpdate}</span>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📡</div>
          <div className="stat-info">
            <h3>Total de Sensores</h3>
            <p className="stat-value">{stats.totalSensors}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <h3>Sensores Ativos</h3>
            <p className="stat-value">{stats.activeSensors}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>Total de Leituras</h3>
            <p className="stat-value">{stats.totalReadings}</p>
          </div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/sensors')}>
          <div className="stat-icon">➕</div>
          <div className="stat-info">
            <h3>Gerenciar</h3>
            <p className="stat-value">Sensores</p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="charts-grid">
        {/* Gráfico de Tipos de Sensor */}
        <div className="chart-card">
          <h2>Distribuição por Tipo</h2>
          {sensorTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={sensorTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={entry => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sensorTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">Nenhum sensor cadastrado</p>
          )}
        </div>

        {/* Gráfico de Últimas Leituras */}
        <div className="chart-card">
          <h2>Últimas Leituras</h2>
          {lastReadingsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={lastReadingsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="no-data">Nenhuma leitura disponível</p>
          )}
        </div>
      </div>

      {/* Tabela de Dados Recentes */}
      <div className="recent-data-card">
        <h2>Dados Recentes</h2>
        {recentData.length > 0 ? (
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sensor</th>
                  <th>Tipo</th>
                  <th>Valor</th>
                  <th>Unidade</th>
                  <th>Data/Hora</th>
                </tr>
              </thead>
              <tbody>
                {recentData.slice(0, 10).map((data, index) => (
                  <tr key={index}>
                    <td>{data.sensorName}</td>
                    <td>{data.sensorType}</td>
                    <td>{data.value}</td>
                    <td>{data.unit || '-'}</td>
                    <td>{new Date(data.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">Nenhum dado recente disponível</p>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
